/**
 * 中标候选人公示 / 成交结果表格增强：
 *
 * 公告 HTML 表格里如果存在"xxx 服务费"列（招标代理服务费 / 中介服务费等）
 * 且列项是数值，则在该列右侧追加一列"报价金额（估算）"，按下列费率表
 * 反推中标金额（单位：万元）。
 *
 * 费率表（中标金额单位：万元；区间左开右闭）：
 *  | 中标金额（万元）             | 费率     |
 *  | ---------------------------- | -------- |
 *  | (0, 100]                     | 1.5%     |
 *  | (100, 500]                   | 1.1%     |
 *  | (500, 1000]                  | 0.8%     |
 *  | (1000, 5000]                 | 0.5%     |
 *  | (5000, 10000]                | 0.25%    |
 *  | (10000, 50000]               | 0.05%    |
 *  | (50000, 100000]              | 0.035%   |
 *  | (100000, 500000]             | 0.008%   |
 *  | (500000, 1000000]            | 0.006%   |
 *  | (1000000, +∞)                | 0.004%   |
 */

interface RateBracket {
  /** 区间下界（不含） */
  low: number;
  /** 区间上界（含） */
  high: number;
  /** 费率（小数） */
  rate: number;
}

const RATE_BRACKETS: ReadonlyArray<RateBracket> = [
  { low: 0,       high: 100,      rate: 0.015 },
  { low: 100,     high: 500,      rate: 0.011 },
  { low: 500,     high: 1000,     rate: 0.008 },
  { low: 1000,    high: 5000,     rate: 0.005 },
  { low: 5000,    high: 10000,    rate: 0.0025 },
  { low: 10000,   high: 50000,    rate: 0.0005 },
  { low: 50000,   high: 100000,   rate: 0.00035 },
  { low: 100000,  high: 500000,   rate: 0.00008 },
  { low: 500000,  high: 1000000,  rate: 0.00006 },
  { low: 1000000, high: Infinity, rate: 0.00004 },
];

/**
 * 根据"服务费（单位：万元）"反推所有自洽的中标金额（单位：万元）。
 *
 * 注意：费率分段表本身存在多解情形 —— 例如服务费 5.5 万元同时与
 * 中标 500 万元（1.1%）和 687.5 万元（0.8%）自洽。返回所有候选，
 * 由 UI 决定如何展示。
 */
function inferAmountsFromFee(feeWan: number): number[] {
  if (!isFinite(feeWan) || feeWan <= 0) return [];
  const out: number[] = [];
  for (const b of RATE_BRACKETS) {
    const amount = feeWan / b.rate;
    if (amount > b.low && amount <= b.high) out.push(amount);
  }
  return out;
}

/** 抓取文本里第一个数值（支持千分位）。 */
function parseNumber(text: string): number | null {
  if (!text) return null;
  const t = text.replace(/\s+/g, "").replace(/，/g, ",");
  const m = t.match(/-?\d[\d,]*\.?\d*/);
  if (!m) return null;
  const n = parseFloat(m[0].replace(/,/g, ""));
  return isFinite(n) ? n : null;
}

/** 把单元格文本解析成"万元"单位的服务费。 */
function parseFeeAsWan(text: string, defaultUnit: "wan" | "yuan"): number | null {
  const num = parseNumber(text);
  if (num === null) return null;
  let unit = defaultUnit;
  // 单元格自身的单位优先于表头
  if (/万元|万/.test(text)) unit = "wan";
  else if (/元/.test(text)) unit = "yuan";
  return unit === "wan" ? num : num / 10000;
}

/** 表头单位识别（默认按"元"处理）。 */
function detectFeeUnit(headerText: string): "wan" | "yuan" {
  return /万元|万/.test(headerText) ? "wan" : "yuan";
}

/** "xxx 服务费"列识别：含"服务费"但不是"费率/比例/标准"等说明列。 */
function isServiceFeeHeader(text: string): boolean {
  if (!text) return false;
  if (!/服务费/.test(text)) return false;
  if (/费率|比例|标准|说明|备注/.test(text)) return false;
  return true;
}

/** 常见表头关键字（用于识别 PDF 跨页后丢失的表头）。 */
const HEADER_KEYWORDS =
  /序号|名称|编号|中标人|候选人|供应商|服务费|金额|单价|总价|单位|项目|包号|分包|标段|代理|备注|说明|联合体|得分|排名/;

/** 一行单元格中"看起来像表头"的判定：关键词命中多、纯数字格少。 */
function rowLooksLikeHeader(row: HTMLTableRowElement): boolean {
  const cells = Array.from(row.children) as HTMLTableCellElement[];
  if (cells.length === 0) return false;
  let kwHits = 0;
  let numCells = 0;
  let nonEmpty = 0;
  for (const c of cells) {
    const t = (c.textContent || "").trim();
    if (!t) continue;
    nonEmpty++;
    if (HEADER_KEYWORDS.test(t)) kwHits++;
    if (/^[\d,.\s%]+$/.test(t)) numCells++;
  }
  if (nonEmpty === 0) return false;
  return kwHits >= 2 && numCells <= 1;
}

/**
 * PDF 跨页表合并：当后一张表的首行不像表头（关键词命中少），
 * 但列数与前一张表一致时，视为跨页延续，把行并入前一张表。
 */
function mergeContinuationTables(root: HTMLElement, doc: Document): boolean {
  const tables = Array.from(root.querySelectorAll("table"));
  let anchor: HTMLTableElement | null = null;
  let anchorCols = 0;
  let merged = false;
  for (const t of tables) {
    const firstRow = (t.querySelector("thead tr") || t.querySelector("tr")) as HTMLTableRowElement | null;
    if (!firstRow) continue;
    const cols = firstRow.children.length;
    const isHeader = rowLooksLikeHeader(firstRow);
    if (isHeader || anchor === null || cols !== anchorCols) {
      anchor = t;
      anchorCols = cols;
      continue;
    }
    // 视为跨页延续：把所有 tr 搬到 anchor 的 tbody 末尾
    let tbody = anchor.querySelector("tbody");
    if (!tbody) {
      tbody = doc.createElement("tbody");
      const stray = Array.from(anchor.children).filter(
        (n) => n.tagName.toLowerCase() === "tr",
      ) as HTMLTableRowElement[];
      stray.forEach((r) => tbody!.appendChild(r));
      anchor.appendChild(tbody);
    }
    const rows = Array.from(t.querySelectorAll("tr")) as HTMLTableRowElement[];
    rows.forEach((r) => tbody!.appendChild(r));
    t.remove();
    merged = true;
  }
  return merged;
}

/** 单个金额格式化：≥10000 万显示为"亿元"。 */
function formatOne(wan: number): string {
  if (wan >= 10000) return `${(wan / 10000).toFixed(2)} 亿元`;
  if (wan >= 100) return `${wan.toFixed(0)} 万元`;
  return `${wan.toFixed(2)} 万元`;
}

/** 表头 tooltip：解释费率表与估算口径。 */
const HEADER_TOOLTIP =
  "按招标代理服务费分段费率反推：\n" +
  "  ≤100 万 1.5% / ≤500 万 1.1% / ≤1000 万 0.8% /\n" +
  "  ≤5000 万 0.5% / ≤1 亿 0.25% / ≤5 亿 0.05% /\n" +
  "  ≤10 亿 0.035% / ≤50 亿 0.008% / ≤100 亿 0.006% /\n" +
  "  >100 亿 0.004%。\n" +
  "费率分段会产生多解，采用最保守（最小）估计。";

/**
 * 对 HTML 字符串中的每个 `<table>`：
 *  - 若表头存在 "xxx 服务费" 列，且至少一个数据行该列为数值
 *  - 在该列右侧追加一列"报价金额（估算）"，按费率反推中标金额
 *
 * 浏览器侧用 `DOMParser` 处理；非浏览器环境或解析失败时原样返回。
 */
export function enrichServiceFeeTable(html: string): string {
  if (!html) return html;
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;
  if (!/<table[\s>]/i.test(html)) return html;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(
      `<!doctype html><html><body><div id="__root__">${html}</div></body></html>`,
      "text/html",
    );
  } catch {
    return html;
  }
  const root = doc.getElementById("__root__");
  if (!root) return html;

  // 先合并 PDF 跨页造成的"无表头延续表"
  const merged = mergeContinuationTables(root, doc);

  let touched = merged;
  const tables = root.querySelectorAll("table");

  tables.forEach((table) => {
    // 表头行：优先 <thead> 第一行，否则取整表第一行
    const headRow =
      table.querySelector("thead tr") ||
      table.querySelector("tr");
    if (!headRow) return;

    const headCells = Array.from(headRow.children) as HTMLTableCellElement[];
    if (headCells.length === 0) return;

    const feeColIdx = headCells.findIndex((c) => isServiceFeeHeader(c.textContent || ""));
    if (feeColIdx < 0) return;

    const feeHeaderText = headCells[feeColIdx]?.textContent || "";
    const headerUnit = detectFeeUnit(feeHeaderText);

    // 数据行：thead 之外或除首行之外的 tr
    let dataRows: HTMLTableRowElement[];
    if (table.tBodies.length > 0) {
      dataRows = Array.from(table.tBodies).flatMap((tb) => Array.from(tb.rows));
    } else {
      dataRows = Array.from(table.rows);
    }
    dataRows = dataRows.filter((r) => r !== headRow);
    if (dataRows.length === 0) return;

    // 至少要有一行该列能解析为正数
    const sample = dataRows
      .map((r) => parseFeeAsWan((r.children[feeColIdx] as HTMLTableCellElement | undefined)?.textContent || "", headerUnit))
      .filter((v): v is number => v !== null && v > 0);
    if (sample.length === 0) return;

    // 检查是否已增强过（幂等，防止重复挂载）
    const next = headCells[feeColIdx]?.nextElementSibling as HTMLElement | null;
    if (next?.getAttribute("data-derived") === "service-fee-amount") return;

    // 给表头追加一格
    const headTag = headCells[feeColIdx].tagName.toLowerCase() === "th" ? "th" : "td";
    const newHead = doc.createElement(headTag) as HTMLTableCellElement;
    newHead.textContent = "报价金额（估算）";
    newHead.title = HEADER_TOOLTIP;
    newHead.setAttribute("data-derived", "service-fee-amount");
    headCells[feeColIdx].after(newHead);

    // 给每个数据行追加一格
    dataRows.forEach((row) => {
      const cells = Array.from(row.children) as HTMLTableCellElement[];
      const target = cells[feeColIdx];
      if (!target) return;
      const tag = target.tagName.toLowerCase() === "th" ? "th" : "td";
      const td = doc.createElement(tag) as HTMLTableCellElement;
      td.setAttribute("data-derived", "service-fee-amount");

      const feeWan = parseFeeAsWan(target.textContent || "", headerUnit);
      if (feeWan === null || feeWan <= 0) {
        td.textContent = "—";
      } else {
        const amounts = inferAmountsFromFee(feeWan);
        if (amounts.length === 0) {
          td.textContent = "—";
        } else {
          // 方案 2：费率表多解时取最小候选（最保守估计）
          const min = Math.min(...amounts);
          td.textContent = `≈ ${formatOne(min)}`;
          if (amounts.length > 1) {
            const sorted = amounts.slice().sort((a, b) => a - b);
            const others = sorted.slice(1).map(formatOne).join(" / ");
            td.title = `按费率分段反推存在多解，已取最保守值。\n其他可能：${others}`;
          } else {
            td.title = HEADER_TOOLTIP;
          }
        }
      }
      target.after(td);
    });

    touched = true;
  });

  return touched ? root.innerHTML : html;
}

// 仅供单元测试 / 调试使用
export const __internals = { inferAmountsFromFee, parseNumber, parseFeeAsWan, RATE_BRACKETS };
